// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { DateInfoRow } from '@/components/date-info-row';
import { Link } from '@/components/link';
import { LocationPairCell } from '@/components/location-pair-cell';
import { StatusChip } from '@/components/status-chip';
import {
  ExpandLess,
  ExpandMore,
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { CYCLE_TIME_SEGMENTS } from '@/fulfillment/components/cycle-time-segments';
import { FulfillmentRowShipments } from '@/fulfillment/components/fulfillment-row-shipments';
import {
  FO_ORDER_STATUS_TOOLTIPS,
  PHASE_CONFIG,
  type ParsedStatus,
  getModifierVariant,
  parseStatus,
} from '@/fulfillment/constants/fulfillment-order-status';
import { getStatusIcon } from '@/fulfillment/constants/fulfillment-order-status-icons';
import { CycleTimeRowMarkers } from '@/report/components/cycle-time-row-markers';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { Fragment, MouseEvent } from 'react';

/**
 * Props for the FulfillmentTableRow component.
 */
interface Props {
  fulfillment: FulfillmentOrder;
  enableRowExpansion?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

/**
 * Truncates text to a maximum number of lines, intelligently handling line breaks.
 * Returns the truncated text and whether truncation was needed.
 *
 * @param text - The text to potentially truncate
 * @param maxLines - Maximum number of lines to display (default: 2)
 * @param maxCharsPerLine - Approximate max characters per line (default: 40)
 * @returns Object with truncated text and whether truncation occurred
 */
function truncateToLines(
  text: string,
  maxLines = 2,
  maxCharsPerLine = 40
): { text: string; isTruncated: boolean } {
  const maxTotalChars = maxLines * maxCharsPerLine;

  // Split by actual line breaks
  const lines = text.split('\n');

  // If we have more actual line breaks than allowed, take first N lines
  let workingText = text;
  let wasTruncatedByLines = false;
  if (lines.length > maxLines) {
    workingText = lines.slice(0, maxLines).join('\n');
    wasTruncatedByLines = true;
  }

  // Always enforce character limit
  if (workingText.length > maxTotalChars) {
    // Try to truncate at a word boundary
    let truncatedText = workingText.substring(0, maxTotalChars);
    const lastSpaceIndex = truncatedText.lastIndexOf(' ');

    // If we found a space and it's not too far back, truncate there
    if (lastSpaceIndex > maxTotalChars * 0.8) {
      truncatedText = truncatedText.substring(0, lastSpaceIndex);
    }

    return {
      text: truncatedText + '...',
      isTruncated: true,
    };
  }

  // If we truncated by lines but not by chars, still need ellipsis
  if (wasTruncatedByLines) {
    return {
      text: workingText + '...',
      isTruncated: true,
    };
  }

  // No truncation needed
  return {
    text,
    isTruncated: false,
  };
}

/**
 * Creates tooltip content showing all fulfillment order dates.
 */
function getAllFulfillmentDatesContent(fulfillment: FulfillmentOrder) {
  const dates = [
    {
      label: 'Created:',
      date: fulfillment.createdAt.toLocaleString({ dateStyle: 'medium' }),
    },
    {
      label: 'Promise ship:',
      date:
        fulfillment.promiseShipDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Promise delivery:',
      date:
        fulfillment.promiseDeliveryDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Shipped:',
      date:
        fulfillment.mostRecentShippedAt?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Delivered:',
      date:
        fulfillment.mostRecentDeliveredAt?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Last updated:',
      date: fulfillment.lastUpdatedAt.toLocaleString({ dateStyle: 'medium' }),
    },
    ...(fulfillment.cancelledAt
      ? [
          {
            label: 'Cancelled:',
            date: fulfillment.cancelledAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={0.25}>
      {dates.map(({ label, date }) => (
        <DateInfoRow key={label} label={label} date={date} />
      ))}
    </Stack>
  );
}

/**
 * Gets the most relevant date for a fulfillment order based on its status.
 * Priority: cancelled → actual delivery → actual ship → promise delivery → promise ship → updated → created
 */
function getRelevantDate(fulfillment: FulfillmentOrder, parsed: ParsedStatus) {
  const { phase } = parsed;

  // 1. Cancelled — show cancelled date
  if (phase === 'cancelled' && fulfillment.cancelledAt) {
    return {
      date: fulfillment.cancelledAt.toLocaleString({ dateStyle: 'medium' }),
      label: null,
    };
  }

  // 2. Actual delivery date (most recent shipment delivered)
  if (fulfillment.mostRecentDeliveredAt) {
    return {
      date: fulfillment.mostRecentDeliveredAt.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Delivered',
    };
  }

  // 3. Actual ship date (most recent shipment shipped)
  if (fulfillment.mostRecentShippedAt) {
    return {
      date: fulfillment.mostRecentShippedAt.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Shipped',
    };
  }

  // 4. Promise delivery date
  if (fulfillment.promiseDeliveryDate) {
    return {
      date: fulfillment.promiseDeliveryDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise delivery',
    };
  }

  // 5. Promise ship date
  if (fulfillment.promiseShipDate) {
    return {
      date: fulfillment.promiseShipDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise ship',
    };
  }

  // 6. Updated != Created — show updated date
  if (
    fulfillment.lastUpdatedAt.toMillis() !== fulfillment.createdAt.toMillis()
  ) {
    return {
      date: fulfillment.lastUpdatedAt.toLocaleString({ dateStyle: 'medium' }),
      label: 'Updated',
    };
  }

  // 7. Created date (fallback)
  return {
    date: fulfillment.createdAt.toLocaleString({ dateStyle: 'medium' }),
    label: 'Created',
  };
}

/**
 * A table row component for displaying fulfillment order information.
 */
export function FulfillmentTableRow(props: Props) {
  const navigate = useNavigate();
  const parsedStatus = parseStatus(props.fulfillment.status);

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.metaKey || event.ctrlKey) {
      window.open(`/fulfillments/${props.fulfillment.key}`, '_blank');
    } else {
      navigate({
        to: '/fulfillments/$fulfillmentKey',
        params: { fulfillmentKey: props.fulfillment.key },
      });
    }
  };

  const handleExpandClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    props.onToggleExpand?.();
  };

  const handleExpandCellClick = (event: MouseEvent<HTMLTableCellElement>) => {
    event.stopPropagation();
  };

  return (
    <>
      <TableRow onClick={handleRowClick} hover sx={{ cursor: 'pointer' }}>
        {/* References Column */}
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">
              {props.fulfillment.fulfillmentOrderNumber}
            </Typography>
            {props.fulfillment.exchangeGroup && (
              <Tooltip
                arrow
                placement="right"
                title={
                  <Box>
                    Part of exchange{' '}
                    <b>{props.fulfillment.exchangeGroup.exchangeNumber}</b>{' '}
                    <br />
                    {props.fulfillment.exchangeGroup.returnOrders.length >
                      0 && (
                      <>
                        {' (linked to '}
                        {props.fulfillment.exchangeGroup.returnOrders.map(
                          (ro, index) => (
                            <Fragment key={ro.key}>
                              {index > 0 && ', '}
                              <Link
                                to="/return-orders/$returnOrderKey"
                                params={{ returnOrderKey: ro.key }}
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  color: 'common.white',
                                  textDecoration: 'underline',
                                }}
                              >
                                {ro.returnOrderNumber}
                              </Link>
                            </Fragment>
                          )
                        )}
                        {')'}
                      </>
                    )}
                  </Box>
                }
              >
                <Chip label="Exchange" size="small" />
              </Tooltip>
            )}
          </Stack>
          {props.fulfillment.project && (
            <Stack
              direction="row"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={0.3}
            >
              <Typography variant="subtitle2">Project:</Typography>
              <Typography variant="body2">
                {props.fulfillment.project.name}
              </Typography>
            </Stack>
          )}
          {props.fulfillment.orderNotes &&
            (() => {
              const { text: truncatedText, isTruncated } = truncateToLines(
                props.fulfillment.orderNotes,
                2,
                60
              );
              return (
                <ConditionalTooltip
                  arrow
                  tooltipText={
                    isTruncated ? (
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {props.fulfillment.orderNotes}
                      </Typography>
                    ) : undefined
                  }
                  placement="top"
                >
                  <Typography variant="body2">
                    <strong>Note:</strong> {truncatedText}
                  </Typography>
                </ConditionalTooltip>
              );
            })()}
        </TableCell>

        {/* From/To Location Column */}
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <LocationPairCell
            fromLocation={props.fulfillment.fromLocation}
            toLocation={props.fulfillment.toLocation}
            showThirdPartyChip
          />
        </TableCell>

        {/* Items Column */}
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <Stack spacing={0.3}>
            {props.fulfillment.items
              .slice(0, props.fulfillment.items.length > 4 ? 3 : 4)
              .map((item) => {
                const ItemIcon = !item.item.hasAssets
                  ? SettingsOutlined
                  : Inventory2Outlined;
                return (
                  <Stack
                    key={item.id}
                    direction="row"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={0.3}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={0.5}
                    >
                      <ItemIcon sx={{ fontSize: '1rem' }} />
                      <>
                        <Typography variant="body2">{item.item.sku}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({item.item.name})
                        </Typography>
                      </>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        &times; {formatNumber(item.requestedQuantity)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            {props.fulfillment.items.length > 4 && (
              <Tooltip
                arrow
                title={
                  <Stack spacing={0.5}>
                    {props.fulfillment.items.slice(3).map((item) => {
                      const itemLabel = `${item.item.sku} (${item.item.name})`;
                      return (
                        <Typography key={item.id} variant="caption">
                          {itemLabel} &times;{' '}
                          {formatNumber(item.requestedQuantity)}
                        </Typography>
                      );
                    })}
                  </Stack>
                }
              >
                <Typography variant="body2" color="text.secondary">
                  +{props.fulfillment.items.length - 3} more
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        {/* Cycle Time Column */}
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <CycleTimeRowMarkers
            segments={CYCLE_TIME_SEGMENTS}
            values={{
              createdToOpened: props.fulfillment.cycleTime.createdToOpenedDays,
              openToFirstShipmentShipped:
                props.fulfillment.cycleTime.openToFirstShipmentShippedDays,
              firstShipmentShippedToClose:
                props.fulfillment.cycleTime.firstShipmentShippedToCloseDays,
            }}
            singularNoun="order"
          />
        </TableCell>

        {/* Status Column */}
        <TableCell sx={{ verticalAlign: 'middle' }}>
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              flexWrap="wrap"
              width="100%"
            >
              <Tooltip
                title={FO_ORDER_STATUS_TOOLTIPS[props.fulfillment.status]}
                arrow
              >
                <StatusChip
                  icon={getStatusIcon(
                    parsedStatus.phase,
                    parsedStatus.modifier
                  )}
                  label={PHASE_CONFIG[parsedStatus.phase].label}
                  color={PHASE_CONFIG[parsedStatus.phase].chipConfig.color}
                  variant={getModifierVariant(
                    parsedStatus.phase,
                    parsedStatus.modifier
                  )}
                />
              </Tooltip>
              {(() => {
                const relevantDate = getRelevantDate(
                  props.fulfillment,
                  parsedStatus
                );
                if (relevantDate.date) {
                  return (
                    <Tooltip
                      arrow
                      placement="left"
                      title={getAllFulfillmentDatesContent(props.fulfillment)}
                    >
                      <Stack alignItems="flex-end">
                        <Typography variant="body2" color="text.secondary">
                          {relevantDate.label}
                        </Typography>
                        <Typography variant="body2">
                          {relevantDate.date}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip
                    arrow
                    placement="left"
                    title={getAllFulfillmentDatesContent(props.fulfillment)}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No date set
                    </Typography>
                  </Tooltip>
                );
              })()}
            </Stack>
          </Stack>
        </TableCell>
        {props.enableRowExpansion && (
          <TableCell
            onClick={handleExpandCellClick}
            sx={{ verticalAlign: 'middle', width: '48px', cursor: 'default' }}
          >
            <IconButton
              onClick={handleExpandClick}
              size="small"
              aria-label={props.isExpanded ? 'Collapse' : 'Expand'}
            >
              {props.isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {props.enableRowExpansion && (
        <TableRow>
          <TableCell
            colSpan={6}
            sx={{
              padding: 0,
              borderBottom: props.isExpanded ? undefined : 'none',
            }}
          >
            <Collapse in={props.isExpanded} timeout="auto" unmountOnExit>
              <FulfillmentRowShipments fulfillment={props.fulfillment} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
